@use '@/styles/tokens' as *;

/* Figma 864:558014. The card itself is `<MapAnchoredPanel chrome="flush">`; this file owns what is
 * inside it, so the header's bottom rule spans the full 405px rather than stopping at a padding. */

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--space-3) var(--space-3) 10px;
  border-bottom: 1px solid var(--border-subtle);
}

.title {
  margin: 0;
  font-size: $font-size-16;
  line-height: 1.2;
  color: var(--text-primary);
}

.viewMore {
  padding: 6px var(--space-2);
  font-size: $font-size-13;
  font-weight: $font-weight-medium;
  line-height: 1.2;
  color: var(--text-primary);
  cursor: pointer;
  background: none;
  border: 1px solid var(--border-subtle);
  border-radius: $radius-md;

  &:hover {
    background: var(--surface-muted);
  }

  /* §13g — visibly distinct from the hover state above. */
  @include focus-ring;
}

/* QA round 2 D5c: 309 total = the header above plus this fixed 228px (Figma builds the 309 from
 * exactly that split). Definite, not `min-height`, so the declared `DEVICE_QUICK_VIEW_SIZE` and the
 * rendered panel agree — `<MapAnchoredPanel>`'s placement geometry (113588 §2b) depends on it. */
.body {
  display: flex;
  height: 228px;
  flex-direction: column;
  gap: var(--space-6);
  align-items: center;
  padding: var(--space-6) var(--space-3) 0;
}

.identity {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
  max-width: 100%;
}

.name {
  display: flex;
  gap: 6px;
  align-items: center;
  max-width: 100%;
  margin: 0;
  font-size: $font-size-20;
  font-weight: $font-weight-medium;
  line-height: 1.2;
  color: var(--text-primary);
}

/* §4d — the panel never widens past its designed width. */
.nameText {
  @include truncate(1);
}

.badge {
  flex: none;
  color: $color-success-900;
}

.model {
  margin: 0;
  font-size: $font-size-12;
  line-height: 1.2;
  color: var(--text-tertiary);
}

/* A list, not a row of divs: three named facts about the Device, always all three (§6a). */
.metrics {
  display: flex;
  gap: var(--space-10);
  justify-content: center;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* §9c — the panel must not shift when a value's rendered width changes (9 → 10, or gaining a minus
 * sign). Figma answers this in the design rather than around it: each slot is a FIXED-width,
 * `shrink-0` box in a 40px-gap row (864:558202 = 89, 864:558219 = 75, 864:558237 = 83), sized by its
 * label — a constant string — and not by its value. Reproduced as a `min-width` rather than a
 * `width` so a font metric that renders "Battery Voltage" wider than Figma's 89px is not clipped.
 * A wider value therefore cannot push its neighbours, resize the row, or move the panel's anchored
 * edge. */
.metric {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-1);
}

.batteryVoltage {
  min-width: 89px;
}

.temperature {
  min-width: 75px;
}

.waterLevel {
  min-width: 83px;
}

.metricLabel {
  margin: 0;
  font-size: $font-size-12;
  line-height: 1.2;
  color: var(--text-muted);
}

.metricValue {
  display: flex;
  gap: var(--space-1);
  align-items: center;
  margin: 0;
}

/* QA round 2 D5a revisits the note below: Figma splits `2.7` (Funnel Display 24) from `V` (Geist
 * 14), and the panel now renders that split — `map-popup-rules.ts`'s `splitReading` does the
 * splitting, PRESENTATION ONLY (§17a still forbids re-deriving the value or the unit itself; nothing
 * here recomputes either). */
.reading {
  font-family: $font-family-heading;
  font-size: $font-size-24;
  font-weight: $font-weight-medium;
  font-variant-numeric: tabular-nums; /* §9c — every digit one advance width, so 8 → 9 cannot jitter. */
  line-height: 1.2;
  color: $color-neutral-900;
  letter-spacing: -0.48px;
}

.unit {
  font-size: $font-size-14;
  font-weight: $font-weight-regular;
  color: var(--text-tertiary);
}

.lastSync {
  display: flex;
  gap: 3px;
  align-items: center;
  justify-content: center;
  margin: 0;
  font-size: $font-size-11;
  line-height: 1.2;
  color: var(--text-muted);
}

/* §5f — a polite live region. Reachable by a screen reader, invisible to everyone else; the same
 * mixin <MetricIndicator>'s state text uses. */
.announcement {
  @include visually-hidden;
}

/* §15a — no Figma frame covers offline (M4). Warning-toned, not error-toned: the connection is
 * degraded, the Device is not. Rides inside `.lastSync`, so it inherits its size and centring. */
.offline {
  display: inline-flex;
  gap: 2px;
  align-items: center;
  margin-left: var(--space-1);
  color: $color-warning-700;
}

/* D5b — the readings-block loading state (Figma `2100:532901`). Sized against the `.metricLabel`/
 * `.reading`/`.lastSync` boxes they stand in for, so the skeleton and the loaded state occupy the
 * same footprint. Same shimmer treatment as this feature's own `device-card-skeleton.tsx`. */
.skeletonLabel {
  display: block;
  width: 70%;
  height: 10px;
  margin-bottom: var(--space-1);
  border-radius: $radius-sm;
  background-color: $color-white-400;

  @include shimmer;
}

.skeletonReading {
  display: block;
  width: 55%;
  height: 20px;
  border-radius: $radius-sm;
  background-color: $color-white-400;

  @include shimmer;
}

.skeletonSync {
  display: block;
  width: 110px;
  height: 10px;
  border-radius: $radius-sm;
  background-color: $color-white-400;

  @include shimmer;
}
